import React, { useState, useEffect, useRef } from "react";
import { MessageCircle, X, Send, Bot, User, Loader2, ChefHat } from "lucide-react";
import { supabase } from "../../lib/supabase";

// ── Fetch all restaurant + menu data from Supabase ────────────────────────────
const buildSystemPrompt = (restaurants, menuItems) => {
  const restLines = restaurants.map((r) => {
    const items = menuItems
      .filter((m) => m.restaurant_id === r.id)
      .map((m) => `    - ${m.name} (${m.category}) — Rs ${m.price}${m.description ? ": " + m.description : ""}`)
      .join("\n");

    return `
Restaurant: ${r.name}
  Location: ${r.location}, ${r.city}
  Cuisine: ${(r.cuisine_type || []).join(", ")}
  Contact: ${r.contact}${r.whatsapp ? " | WhatsApp: " + r.whatsapp : ""}
  Hours: ${r.opening_time} – ${r.closing_time}
  Currently: ${r.is_open ? "Open" : "Closed"}
  Rating: ${r.rating > 0 ? r.rating.toFixed(1) + "/5 (" + r.review_count + " reviews)" : "No reviews yet"}
  Min Order: ${r.min_order > 0 ? "Rs " + r.min_order : "None"}
  Delivery Fee: ${r.delivery_fee > 0 ? "Rs " + r.delivery_fee : "Free"}
  ${r.description ? "About: " + r.description : ""}
  Menu:
${items || "    (No menu items listed yet)"}`;
  }).join("\n---");

  return `You are a friendly and helpful AI food assistant for Khana Spot — a food ordering platform serving Burewala and Vehari, Pakistan.

Your job is to help customers:
- Find restaurants by cuisine, location, price, or rating
- Explore menus and suggest dishes
- Answer questions about delivery, hours, and contact info
- Help them decide what to order
- Guide them to place orders on the website

Always be warm, helpful, and concise. Respond in the same language the user writes in (English or Urdu). If they write in Urdu, reply in Urdu.

When recommending food, be enthusiastic and specific. Mention prices clearly in Rs (Pakistani Rupees).

You CANNOT place orders directly — tell users to add items to their cart on the website. You CAN tell them which restaurant page to visit.

If asked something unrelated to food or the platform, politely redirect the conversation.

Current date/time context: ${new Date().toLocaleString("en-PK", { timeZone: "Asia/Karachi" })}

═══════════════════════════════════════
PLATFORM DATA — ALL RESTAURANTS & MENUS
═══════════════════════════════════════
${restLines || "No restaurants available yet."}
═══════════════════════════════════════`;
};

// ── Single chat message bubble ────────────────────────────────────────────────
const MessageBubble = ({ msg }) => {
  const isUser = msg.role === "user";
  return (
    <div className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : "flex-row"}`}>
      {/* Avatar */}
      <div className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-bold mt-0.5 ${isUser ? "bg-orange-500" : "bg-gradient-to-br from-blue-500 to-purple-600"}`}>
        {isUser ? <User className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
      </div>
      {/* Bubble */}
      <div className={`max-w-[78%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
        isUser
          ? "bg-orange-500 text-white rounded-tr-sm"
          : "bg-white text-gray-800 border border-gray-100 shadow-sm rounded-tl-sm"
      }`}>
        {msg.content}
      </div>
    </div>
  );
};

// ── Typing indicator ──────────────────────────────────────────────────────────
const TypingIndicator = () => (
  <div className="flex gap-2.5">
    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
      <Bot className="w-3.5 h-3.5 text-white" />
    </div>
    <div className="bg-white border border-gray-100 shadow-sm rounded-2xl rounded-tl-sm px-4 py-3 flex items-center gap-1">
      {[0, 1, 2].map((i) => (
        <div key={i} className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
      ))}
    </div>
  </div>
);

// ── Quick suggestion chips ────────────────────────────────────────────────────
const SUGGESTIONS = [
  "What restaurants are open now?",
  "Show me biryani options",
  "Best rated restaurants?",
  "Cheapest delivery options?",
];

// ── Main AIAssistant component ────────────────────────────────────────────────
export default function AIAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hi! 👋 I'm your Khana Spot assistant. I can help you find restaurants, explore menus, and decide what to order in Burewala and Vehari. What are you craving today?",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState("");
  const [hasUnread, setHasUnread] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  // ── Load restaurant + menu data once when chat opens ─────────────────────
  useEffect(() => {
    if (!open || dataLoaded) return;

    const loadData = async () => {
      const [{ data: restaurants }, { data: menuItems }] = await Promise.all([
        supabase.from("restaurants").select("*").eq("is_active", true).order("rating", { ascending: false }),
        supabase.from("menu_items").select("*").eq("is_available", true),
      ]);
      const prompt = buildSystemPrompt(restaurants || [], menuItems || []);
      setSystemPrompt(prompt);
      setDataLoaded(true);
    };

    loadData();
  }, [open, dataLoaded]);

  // ── Auto-scroll to latest message ─────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // ── Focus input when opened ───────────────────────────────────────────────
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
      setHasUnread(false);
    }
  }, [open]);

  // ── Send message to OpenAI ────────────────────────────────────────────────
  const sendMessage = async (text) => {
    const userText = text.trim();
    if (!userText || loading) return;

    const userMsg = { role: "user", content: userText };
    const updatedMessages = [...messages, userMsg];

    setMessages(updatedMessages);
    setInput("");
    setLoading(true);

    try {
      const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
      if (!apiKey) throw new Error("OpenAI API key not configured.");

      // Build messages array for OpenAI
      const openaiMessages = [
        { role: "system", content: systemPrompt || "You are a helpful food ordering assistant for Khana Spot in Pakistan." },
        // Send last 10 messages only to keep costs low
        ...updatedMessages.slice(-10),
      ];

      abortRef.current = new AbortController();

      const response = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
        },
        signal: abortRef.current.signal,
        body: JSON.stringify({
          model: "gpt-4o-mini",        // cheapest + fast, great for this use case
          messages: openaiMessages,
          max_tokens: 500,             // keep responses concise
          temperature: 0.7,
          stream: false,
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error?.message || "OpenAI request failed");
      }

      const data = await response.json();
      const reply = data.choices?.[0]?.message?.content || "Sorry, I couldn't get a response. Please try again.";

      setMessages((prev) => [...prev, { role: "assistant", content: reply }]);

      // If chat is closed, show unread indicator
      if (!open) setHasUnread(true);
    } catch (err) {
      if (err.name === "AbortError") return;
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Sorry, something went wrong. Please check your internet connection and try again. 🙏",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleSuggestion = (text) => {
    sendMessage(text);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const handleClose = () => {
    abortRef.current?.abort();
    setOpen(false);
  };

  const showSuggestions = messages.length === 1 && !loading;

  return (
    <>
      {/* ── Floating button ─────────────────────────────────────────────── */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-gradient-to-br from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-full shadow-2xl flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95"
          aria-label="Open AI Assistant"
        >
          <MessageCircle className="w-6 h-6" />
          {hasUnread && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse" />
          )}
        </button>
      )}

      {/* ── Chat window ─────────────────────────────────────────────────── */}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[370px] max-w-[calc(100vw-24px)] h-[560px] max-h-[calc(100vh-100px)] bg-gray-50 rounded-3xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden animate-scale-in">

          {/* Header */}
          <div className="bg-gradient-to-r from-orange-500 to-red-500 px-4 py-3.5 flex items-center gap-3 flex-shrink-0">
            <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center">
              <ChefHat className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-white font-semibold text-sm leading-tight">Lokahi AI Assistant</p>
              <p className="text-orange-100 text-xs">
                {dataLoaded ? "● Online — Ask me anything!" : "● Loading menu data..."}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="text-white/80 hover:text-white hover:bg-white/10 p-1.5 rounded-xl transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map((msg, i) => (
              <MessageBubble key={i} msg={msg} />
            ))}

            {loading && <TypingIndicator />}

            {/* Quick suggestions — shown only at start */}
            {showSuggestions && (
              <div className="pt-2">
                <p className="text-xs text-gray-400 text-center mb-2">Quick questions</p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => handleSuggestion(s)}
                      className="text-xs bg-white border border-orange-200 text-orange-600 hover:bg-orange-50 px-3 py-1.5 rounded-full transition-colors font-medium"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="flex-shrink-0 px-3 py-3 border-t border-gray-200 bg-white">
            <form onSubmit={handleSubmit} className="flex gap-2 items-end">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about restaurants, menus, prices..."
                rows={1}
                disabled={loading}
                className="flex-1 resize-none px-3.5 py-2.5 text-sm border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all bg-gray-50 disabled:opacity-50 max-h-24"
                style={{ lineHeight: "1.4" }}
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="w-10 h-10 flex-shrink-0 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-2xl flex items-center justify-center transition-all active:scale-95"
              >
                {loading
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Send className="w-4 h-4" />
                }
              </button>
            </form>
            <p className="text-center text-xs text-gray-300 mt-1.5">Powered by OpenAI · Khana Spot</p>
          </div>
        </div>
      )}
    </>
  );
}
